import React, { useState } from 'react';
import { useLens } from './LensContext';
import SectionHeader from './SectionHeader';
import './Projects.css';

import nykaaPdf from './img/Nykaa.pdf';
import youtubeHypePdf from './img/youtubeHype.pdf';
import nykaaCover from './img/nykaa_cover.png';
import youtubeCover from './img/Youtubehype.png';

// Every case file carries two framings of the same work:
// strategist = problem → insight → outcome, builder = architecture → stack → decisions.
const projects = [
  {
    type: 'Web Application',
    title: 'SAGAR',
    caption: 'SAGAR, marine intelligence workspace',
    image: require('./img/SAGAR.png'),
    github: 'https://github.com/Technical-Mavle/sagar',
    link: 'https://sagar-v1.netlify.app',
    video: 'https://drive.google.com/file/d/1IixwOJW0ktL9nHqBxQlKwBZEwWcU40X-/view?usp=drive_link',
    stamp: '1st of 68,766 · SIH ’25',
    lenses: {
      strategist: {
        summary:
          'India’s ocean researchers had biodiversity and oceanographic data locked in silos, so cross-domain questions took weeks to answer. SAGAR unifies those datasets into one interactive workspace with an AI research assistant. Judged India’s best solution at SIH 2025.',
        tags: ['Problem Discovery', 'GovTech', 'AI Product', 'Data UX'],
      },
      builder: {
        summary:
          'React + Three.js front end over Supabase, with a RAG pipeline that grounds LLM answers in curated oceanographic datasets. Interactive geospatial visualisation and retrieval tuned for scientific accuracy rather than fluency.',
        tags: ['React', 'Three.js', 'RAG', 'Supabase', 'AI/ML'],
      },
    },
  },
  {
    type: 'Product Case Study',
    title: 'Nykaa Product Analysis',
    caption: 'Nykaa, e-commerce funnel teardown',
    image: nykaaCover,
    link: nykaaPdf,
    isPdf: true,
    lenses: {
      strategist: {
        summary:
          'A teardown of Nykaa’s e-commerce experience: where discovery leaks, why retention stalls, and a prioritised set of recommendations, each tied to the metric it should move.',
        tags: ['Product Strategy', 'UX Research', 'Market Analysis', 'E-commerce'],
      },
      builder: {
        summary:
          'The rigour behind the strategy: competitive benchmarking, heuristic UX evaluation, and a measurement framework (activation, AOV, repeat rate) attached to every proposed fix so it can be validated, not just admired.',
        tags: ['Funnel Analysis', 'Benchmarking', 'Metrics Design', 'Experiment Thinking'],
      },
    },
  },
  {
    type: 'Web Application',
    title: 'PARAS',
    caption: 'PARAS, smart parking automation',
    image: require('./img/PARAS.png'),
    github: 'https://github.com/tanmayrajurkar/PARAS',
    link: 'http://paras-v1.netlify.app/',
    stamp: 'Winner · SIH ’24',
    lenses: {
      strategist: {
        summary:
          'Cities lose hours and fuel to circling for parking. PARAS turns any lot into a smart lot with live space detection, automated entry, and predictive availability. Pitched and productised to a national win at SIH 2024 against 49,000+ teams.',
        tags: ['Smart Cities', 'IoT Product', 'Systems Thinking', 'Go-to-Market'],
      },
      builder: {
        summary:
          'Python ML backend doing hardware-based parking space detection, wired to IoT controllers and a React dashboard that streams real-time occupancy and availability predictions.',
        tags: ['Python', 'ML', 'IoT', 'React'],
      },
    },
  },
  {
    type: 'Product Case Study',
    title: 'YouTube Hype',
    caption: 'YouTube Hype, discovery feature strategy',
    image: youtubeCover,
    link: youtubeHypePdf,
    isPdf: true,
    lenses: {
      strategist: {
        summary:
          'Feature strategy for YouTube’s discovery problem: how to surface rising creators before the algorithm does, with engagement loops designed around anticipation instead of raw watch time.',
        tags: ['Product Strategy', 'UX Design', 'Content Platform', 'User Engagement'],
      },
      builder: {
        summary:
          'Concept taken to spec: engagement loop design, proposed ranking signals for “hype” propagation through recommendations, and wireframed flows detailed enough to hand to an engineering team.',
        tags: ['Feature Spec', 'Ranking Signals', 'Wireframing', 'Engagement Loops'],
      },
    },
  },
];

const sectionIntro = {
  strategist: 'Outcomes first: what shipped, and what it changed.',
  builder: 'Under the hood: the architecture and the decisions behind it.',
};

function Projects() {
  const { lens } = useLens();
  const [selectedPdf, setSelectedPdf] = useState(null);

  const handleLinkClick = (project) => {
    if (project.isPdf) {
      setSelectedPdf(project.link);
    } else {
      window.open(project.link, '_blank');
    }
  };

  return (
    <>
      <SectionHeader index="03" label="Case files" title="Selected works" />
      <p key={lens} className="projects-intro lens-fade">{sectionIntro[lens]}</p>

      <div className="case-files">
        {projects.map((project, index) => {
          const facet = project.lenses[lens];
          const fileNo = String(index + 1).padStart(2, '0');
          return (
            <article key={project.title} className="case-file reveal">
              <div className="case-head">
                <span className="mono-label">
                  <span className="case-file-no">File {fileNo}</span> / {project.type}
                </span>
                {project.stamp && <span className="stamp case-stamp">{project.stamp}</span>}
              </div>

              <div className="case-grid">
                <div className="case-info">
                  <h3 className="case-title">{project.title}</h3>

                  <div key={lens} className="lens-fade">
                    <p className="case-summary">{facet.summary}</p>
                    <dl className="case-meta">
                      <div className="case-meta-row">
                        <dt>{lens === 'builder' ? 'Stack' : 'Focus'}</dt>
                        <dd>{facet.tags.join(' · ')}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="case-actions">
                    <button className="btn-doc btn-doc--accent" onClick={() => handleLinkClick(project)}>
                      {project.isPdf ? 'Read case study' : 'View live'} ↗
                    </button>
                    {project.github && (
                      <a href={project.github} target="_blank" rel="noopener noreferrer" className="btn-doc">
                        Source
                      </a>
                    )}
                    {project.video && (
                      <a href={project.video} target="_blank" rel="noopener noreferrer" className="btn-doc">
                        Demo
                      </a>
                    )}
                  </div>
                </div>

                <figure className="case-figure" onClick={() => handleLinkClick(project)}>
                  <div className="case-figure-frame">
                    <img src={project.image} alt={project.title} />
                  </div>
                  <figcaption className="mono-label">
                    fig. {fileNo} · {project.caption}
                  </figcaption>
                </figure>
              </div>
            </article>
          );
        })}
      </div>

      {/* PDF Modal */}
      {selectedPdf && (
        <div className="pdf-modal-overlay" onClick={() => setSelectedPdf(null)}>
          <div className="pdf-modal-content" onClick={e => e.stopPropagation()}>
            <button className="pdf-modal-close" onClick={() => setSelectedPdf(null)}>&times;</button>
            <iframe
              src={selectedPdf}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title="Case Study PDF"
            />
          </div>
        </div>
      )}
    </>
  );
}

export default Projects;
